import React, { useState } from "react";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

export default function CourseEnrollmentForm({ courseSlug, courseTitle }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    qualification: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/course-enrollments", {
        ...form,
        course_slug: courseSlug,
        course_title: courseTitle,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-2xl border border-green-200 bg-green-50 p-6 text-center"
        data-testid="enrollment-success"
      >
        <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto mb-3" />
        <h4 className="text-lg font-bold text-green-800 mb-1">Thanks, {form.full_name.split(" ")[0]}!</h4>
        <p className="text-sm text-green-700">
          Our admissions team will reach out about <strong>{courseTitle}</strong> within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 space-y-4"
      data-testid="course-enrollment-form"
    >
      <div>
        <h4 className="text-lg font-bold text-[#0B2A6B]">Enrol for this course</h4>
        <p className="text-xs text-slate-500">Get a callback within 24 hours.</p>
      </div>

      <Field label="Full name" required>
        <input
          type="text"
          value={form.full_name}
          onChange={(e) => onChange("full_name", e.target.value)}
          required
          className="form-input"
          data-testid="enroll-name"
          placeholder="Your name"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Email" required>
          <input
            type="email"
            value={form.email}
            onChange={(e) => onChange("email", e.target.value)}
            required
            className="form-input"
            data-testid="enroll-email"
            placeholder="you@example.com"
          />
        </Field>
        <Field label="Phone" required>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            required
            className="form-input"
            data-testid="enroll-phone"
            placeholder="+91 ..."
          />
        </Field>
      </div>

      <Field label="Qualification">
        <select
          value={form.qualification}
          onChange={(e) => onChange("qualification", e.target.value)}
          className="form-input"
          data-testid="enroll-qualification"
        >
          <option value="">Select…</option>
          <option value="10th Pass">10th Pass</option>
          <option value="12th Pass">12th Pass</option>
          <option value="ITI">ITI</option>
          <option value="Diploma">Diploma</option>
          <option value="Degree">Degree</option>
          <option value="Other">Other</option>
        </select>
      </Field>

      <Field label="Message (optional)">
        <textarea
          rows={3}
          value={form.message}
          onChange={(e) => onChange("message", e.target.value)}
          className="form-input"
          data-testid="enroll-message"
          placeholder="Anything else we should know?"
        />
      </Field>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2" data-testid="enroll-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg btn-primary disabled:opacity-60"
        data-testid="enroll-submit"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending…
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            Request a callback
          </>
        )}
      </button>

      <p className="text-[11px] text-slate-400 text-center">
        By submitting, you agree to be contacted by Kiki Centre.
      </p>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
    </label>
  );
}
